/**
 * Incident Editor Dialog Component (PROJ-007)
 * Complete CRM incident management with privacy/compliance
 * REFACTORED: Accordion-based layout for better UX
 * Tabs: Data (accordion with 8 sections), Notes, Payload, Timeline, SLA
 * MODES: create, edit, view (with switch to edit capability)
 */

import { useState } from 'react';
import { Database, MessageSquare, Clock, Timer, Code, GitMerge } from 'lucide-react';
import {
  DraggableDialog,
  DraggableDialogContent,
} from '@/components/ui/draggable-dialog';
import { Form } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { Header } from './Header';
import { FooterActions } from './FooterActions';
import { IncidentDataTab } from './IncidentDataTab';
import { NotesTab } from './NotesTab';
import { PayloadTab } from './PayloadTab';
import { TimelineTab } from './TimelineTab';
import { SlaTab } from './SlaTab';
import { MergeHistoryTab } from './MergeHistoryTab';
import { useIncidentEditorForm } from './useIncidentEditorForm';
import { isIncidentReadOnly } from '@/components/incidents/KanbanView';
import type { IncidentEditorDialogProps, IncidentEditorMode } from './types';

export function IncidentEditorDialog({
  open,
  mode: initialMode,
  incident,
  onClose,
  onSubmit,
  onMerge,
}: IncidentEditorDialogProps) {
  // Allow switching from view to edit mode
  const [currentMode, setCurrentMode] = useState<IncidentEditorMode>(initialMode);

  // Reset mode when dialog reopens
  useState(() => {
    if (open) {
      setCurrentMode(initialMode);
    }
  });

  const {
    form,
    submitError,
    setSubmitError,
    firstFieldRef,
    handleSubmit,
    handleSaveAndClose,
    isEditMode,
  } = useIncidentEditorForm({
    mode: currentMode,
    incident: incident || null,
    open,
    onSubmit,
    onClose,
  });

  const { handleClose: requestClose, UnsavedChangesDialog } = useUnsavedChanges({
    isDirty: currentMode !== 'view' && form.formState.isDirty,
    onClose,
  });

  const handleSwitchToEdit = () => {
    setCurrentMode('edit');
  };

  const isViewMode = currentMode === 'view';
  // PROJ-036: Can't switch to edit if incident is resolved/cancelled (must reactivate first)
  const canSwitchToEdit = isViewMode && incident && !isIncidentReadOnly(incident);
  // PROJ-052: Can merge if incident is not cancelled and not already merged
  const canMerge: boolean = isViewMode && !!incident && incident.status !== 'cancelled' && incident.statusReason !== 'merged';

  return (
    <DraggableDialog open={open} onOpenChange={(next) => !next && requestClose()}>
      <DraggableDialogContent className="flex flex-col sm:max-w-[800px] max-h-[90vh]">
        <Header
          mode={currentMode}
          onSwitchToEdit={canSwitchToEdit ? handleSwitchToEdit : undefined}
          onMerge={onMerge && incident ? () => onMerge(incident) : undefined}
          canMerge={canMerge}
        />

        <Form {...form}>
          <form
            onSubmit={(e) => {
              form.handleSubmit(handleSubmit)(e);
            }}
            className="flex flex-1 min-h-0 flex-col overflow-hidden"
          >
            <div
              className="flex flex-col flex-1 min-h-0 overflow-auto overscroll-contain no-drag"
              onWheelCapture={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              data-dialog-drag-cancel="true"
              style={{ touchAction: 'pan-y' }}
            >
              <Tabs defaultValue="data" className="flex flex-col flex-1 min-h-0 max-h-full overflow-hidden w-full">
                {/* TabsList stays OUTSIDE fieldset so tabs remain navigable in View Mode */}
                <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 border-b bg-transparent px-6 py-3 z-[100]">
                  <TabsTrigger value="data" className="flex items-center gap-2 px-3 py-2 text-sm">
                    <Database className="h-4 w-4 shrink-0 opacity-60" />
                    <span>Datos del Incidente</span>
                  </TabsTrigger>
                  <TabsTrigger value="notes" className="flex items-center gap-2 px-3 py-2 text-sm">
                    <MessageSquare className="h-4 w-4 shrink-0 opacity-60" />
                    <span>Notas</span>
                  </TabsTrigger>
                  <TabsTrigger value="payload" className="flex items-center gap-2 px-3 py-2 text-sm">
                    <Code className="h-4 w-4 shrink-0 opacity-60" />
                    <span>Payload</span>
                  </TabsTrigger>
                  <TabsTrigger value="timeline" className="flex items-center gap-2 px-3 py-2 text-sm">
                    <Clock className="h-4 w-4 shrink-0 opacity-60" />
                    <span>Historial</span>
                  </TabsTrigger>
                  <TabsTrigger value="sla" className="flex items-center gap-2 px-3 py-2 text-sm">
                    <Timer className="h-4 w-4 shrink-0 opacity-60" />
                    <span>SLA</span>
                  </TabsTrigger>
                  <TabsTrigger value="merge-history" className="flex items-center gap-2 px-3 py-2 text-sm">
                    <GitMerge className="h-4 w-4 shrink-0 opacity-60" />
                    <span>Fusiones</span>
                  </TabsTrigger>
                </TabsList>

                {/* fieldset only wraps the content area so form fields are disabled in View Mode */}
                <fieldset disabled={isViewMode} className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  <TabsContent value="data" className="flex flex-col flex-1 min-h-0 overflow-y-auto z-[50]">
                    <IncidentDataTab
                      form={form}
                      isEditMode={isEditMode}
                      incidentId={incident?.id}
                      isViewMode={isViewMode}
                      // PROJ-020: Pass audit info for display
                      auditInfo={incident ? {
                        createdAt: incident.createdAt,
                        updatedAt: incident.updatedAt,
                        createdByName: incident.createdBy?.fullName || null,
                        updatedByName: incident.updatedBy?.fullName || null,
                      } : undefined}
                    />
                  </TabsContent>

                  <TabsContent value="notes" className="flex flex-col flex-1 min-h-0 overflow-y-auto px-6 py-4 z-[50]">
                    <NotesTab incidentId={incident?.id || null} />
                  </TabsContent>

                  <TabsContent value="payload" className="flex flex-col flex-1 min-h-0 overflow-y-auto px-6 py-4 z-[50]">
                    <PayloadTab form={form} />
                  </TabsContent>

                  <TabsContent value="timeline" className="flex flex-col flex-1 min-h-0 overflow-y-auto px-6 py-4 z-[50]">
                    <TimelineTab incidentId={incident?.id || null} />
                  </TabsContent>

                  <TabsContent value="sla" className="flex flex-col flex-1 min-h-0 overflow-y-auto px-6 py-4 z-[50]">
                    <SlaTab incidentId={incident?.id || null} />
                  </TabsContent>

                  <TabsContent value="merge-history" className="flex flex-col flex-1 min-h-0 overflow-y-auto px-6 py-4 z-[50]">
                    <MergeHistoryTab incidentId={incident?.id || null} />
                  </TabsContent>
                </fieldset>
              </Tabs>
            </div>

            {!isViewMode && (
              <FooterActions
                form={form}
                submitError={submitError}
                setSubmitError={setSubmitError}
                handleSaveAndClose={handleSaveAndClose}
                requestClose={requestClose}
              />
            )}
          </form>
        </Form>

        {UnsavedChangesDialog}
      </DraggableDialogContent>
    </DraggableDialog>
  );
}
